// Layer 3 (Transformations): pure projection for the Advisory rail chrome.
//
// #732 — the rail badge and the Advisory strip's Cases cell name different
// quantities, so the badge names what it counts. It is not a seed constant and
// it is never recomputed here: it sums the open-case counts the strip's own
// engine read publishes (`useAdvisoryFlowRead` → `flowLandingRead`), so badge
// and strip can only ever differ in scope, never in basis.

import type { FlowLandingRead } from '@/versions/v1/modules/advisory/FlowLanding.transform';
import type { PipelineStageId } from '@/versions/v1/modules/advisory/PipelineStrip.transform';

/**
 * The stages on which a case is still open — the landing's own `openCaseRows`
 * predicate (`cases`, `diagnosis`, `action`). A case that has reached Value has
 * been captured; one at Learn has been closed out. Signals and excursions sit
 * upstream of a case and are not cases at all.
 */
export const ADVISORY_OPEN_STAGES: readonly PipelineStageId[] = ['cases', 'diagnosis', 'action'];

/** What the rail badge counts, and the words that name it. */
export type AdvisoryRailBadge = { count: number; label: string };

/**
 * The rail badge, read from the flow read the Advisory strip paints. Null until
 * the read exists — every case fixture's replay frames in hand, the same gate
 * the strip waits behind — so the rail never renders a figure the strip cannot.
 */
export function advisoryRailBadge(read: FlowLandingRead | null): AdvisoryRailBadge | null {
  if (!read) return null;
  const count = ADVISORY_OPEN_STAGES.reduce((total, stage) => total + read.counts[stage], 0);
  return { count, label: `${count} open case${count === 1 ? '' : 's'} in Advisory` };
}
